import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AuthenticationProvider from '../providers/AuthenticationProvider';
import './Login.css'; // Reuse login styles

const ForgotPassword = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const authProvider = new AuthenticationProvider();

    const [email, setEmail] = useState('');
    const [step, setStep] = useState(1); // 1: Email entry, 2: Code & New Password entry
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleRequestCode = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setMessage('');

        const result = await authProvider.requestPasswordReset(email.trim());
        setLoading(false);

        // Always proceed to step 2 to prevent user enumeration
        setStep(2);
        setMessage(t('reset_code_sent_if_exists') || 'If an account exists with this email, a reset code has been sent.');
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        // Password complexity check (matching Register.js)
        const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d).{7,}$/; 
        if (!passwordRegex.test(newPassword.trim())) {
            setError(t('password_complexity_error') || 'Your password shall be at least 7 characters and shall contain at least 1 number and 1 letter');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError(t('passwords_not_matching') || 'Passwords do not match.');
            return;
        }

        setLoading(true);
        setError('');

        const result = await authProvider.resetPassword(email.trim(), code.trim(), newPassword.trim());
        setLoading(false);

        if (result && !result.error) {
            setMessage(t('password_reset_success') || 'Your password has been reset successfully. Redirecting to login...');
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } else {
            setError(t('invalid_or_expired_code') || 'Invalid or expired reset code.');
        }
    };

    const handleCodeChange = (e) => {
        const val = e.target.value.replace(/\D/g, ''); // Only allow numbers
        setCode(val);
    };

    return (
        <div className="login-container">
            <h2>{t('forgot_password')}</h2>
            
            {step === 1 ? (
                <form className="login-form" onSubmit={handleRequestCode}>
                    <p style={{ color: 'white', marginBottom: '15px' }}>
                        {t('enter_email_for_reset') || 'Enter your email to receive a 6-digit reset code.'}
                    </p>
                    <label>
                        {t('email')}:
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </label>
                    <div className="login-buttons">
                        <button type="submit" disabled={loading}>
                            {loading ? t('loading') : t('send_code') || 'Send Code'}
                        </button>
                        <button type="button" onClick={() => navigate('/login')}>
                            {t('back')}
                        </button>
                    </div>
                </form>
            ) : (
                <form className="login-form" onSubmit={handleResetPassword}>
                    <p style={{ color: '#4facfe', marginBottom: '15px', fontWeight: 'bold' }}>
                        {message}
                    </p>
                    <label>
                        {t('reset_code') || '6-Digit Code'}:
                        <input
                            type="text"
                            value={code}
                            onChange={handleCodeChange}
                            maxLength="6"
                            required
                        />
                    </label>
                    <label>
                        {t('new_password') || 'New Password'}:
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                        />
                    </label>
                    <label>
                        {t('confirm_password') || 'Confirm New Password'}:
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                        />
                    </label>
                    <div className="login-buttons">
                        <button type="submit" disabled={loading}>
                            {loading ? t('loading') : t('reset_password') || 'Reset Password'}
                        </button>
                    </div>
                </form>
            )}

            {error && (
                <div style={{ color: '#ff4b2b', marginTop: '15px', fontWeight: 'bold', textAlign: 'center' }}>
                    {error}
                </div>
            )}
            
            {step === 2 && !error && !message.includes('successfully') && (
                <p style={{ color: 'white', marginTop: '15px', fontSize: '14px', textAlign: 'center' }}>
                    {t('didnt_receive_code') || "Didn't receive a code?"} <span style={{ color: '#4facfe', cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setStep(1)}>{t('try_again') || 'Try again'}</span>
                </p>
            )}
        </div>
    );
};

export default ForgotPassword;
